import type { TemplateOption } from "../hooks/useTemplateOptions";
import { LC_KEYS } from "./constants";

/**
 * Why the one-click Start card names the template it does, best evidence first:
 * - "chosen": picked on the card for this start;
 * - "last_used": the template last picked on this device (what the drawer pre-fills);
 * - "recent_note": the template on the clinician's most recent note, for a new device, a
 *   cleared browser, or a re-login (signing out wipes local storage);
 * - "only_template": the clinician has exactly one template of their own to choose from.
 * With none of these the card starts anyway and the template is chosen when the recording
 * is submitted, through the same details drawer record-first uses.
 */
export type SessionTemplateSource = "chosen" | "last_used" | "recent_note" | "only_template";

export type SessionTemplate = {
  /** What goes into ``recordingDetail.note_type``. */
  value: number | string;
  name: string;
  /** Gallery template: the backend copies it into "My Templates" when the session starts. */
  isTemplate: boolean;
  templateId?: number;
  source: SessionTemplateSource;
};

/** The template last picked on this device, or null. Gallery picks are never stored. */
export const readStoredTemplateChoice = (): number | null => {
  if (localStorage.getItem(LC_KEYS.IS_TEMPLATE) === "true") return null;
  const raw = localStorage.getItem(LC_KEYS.NOTE_TYPE);
  if (raw == null || raw === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

/**
 * The visit details the drawer pre-fills from this device (CPT duration and location), read the
 * way the drawer reads them. A Quick Start with a template skips the drawer, so it carries these
 * itself; without them the note would be generated with fewer details than a drawer start.
 */
export const readStoredVisitDetails = (): { duration_type?: number | null; location_type?: number } => {
  const details: { duration_type?: number | null; location_type?: number } = {};
  const cpt = localStorage.getItem(LC_KEYS.CPT_CODE);
  if (cpt) details.duration_type = cpt === "undefined" ? null : Number(cpt);
  const location = localStorage.getItem(LC_KEYS.LOCATION_TYPE);
  if (location) details.location_type = Number(location);
  return details;
};

/** Remember a picker choice so the next drawer (and the Start card) pre-fills it. */
export const rememberTemplateChoice = (value: number | string, isGalleryTemplate: boolean): void => {
  if (!isGalleryTemplate) {
    localStorage.setItem(LC_KEYS.NOTE_TYPE, value.toString());
    localStorage.setItem(LC_KEYS.IS_TEMPLATE, "false");
  } else {
    localStorage.removeItem(LC_KEYS.NOTE_TYPE);
    localStorage.removeItem(LC_KEYS.IS_TEMPLATE);
  }
};

const toSessionTemplate = (option: TemplateOption, source: SessionTemplateSource): SessionTemplate => ({
  value: option.value,
  name: option.searchText,
  isTemplate: Boolean(option.is_template),
  templateId: option.is_template ? option.id : undefined,
  source,
});

/**
 * The template a one-click start will use, or null when there is no evidence. Every
 * candidate is checked against the picker's current options, so a template that has
 * been deleted or unshared since it was last used is skipped rather than sent.
 */
export const resolveSessionTemplate = ({
  options,
  chosen,
  stored,
  recent,
}: {
  options: TemplateOption[];
  chosen?: number | string | null;
  stored?: number | null;
  recent?: number[] | null;
}): SessionTemplate | null => {
  const find = (value: number | string | null | undefined) =>
    value == null ? undefined : options.find((option) => option.value === value);

  const picked = find(chosen);
  if (picked) return toSessionTemplate(picked, "chosen");

  // The gallery is a catalogue, not a choice the clinician made: never infer one from it.
  const own = (value: number | string | null | undefined) => {
    const option = find(value);
    return option && option.group !== "gallery" ? option : undefined;
  };

  const last = own(stored);
  if (last) return toSessionTemplate(last, "last_used");

  for (const value of recent ?? []) {
    const option = own(value);
    if (option) return toSessionTemplate(option, "recent_note");
  }

  const mine = options.filter((option) => option.group !== "gallery");
  if (mine.length === 1) return toSessionTemplate(mine[0], "only_template");

  return null;
};

/**
 * What the Start card hands the recorder (``startQuickCapture``). With neither field it is
 * plain record-first: the sidebar's patient, no template.
 */
export type QuickCapturePreset = {
  /** Record for this patient instead of the sidebar's selection. */
  client?: { id: number; uuid?: string | null; name?: string | null } | null;
  /** Record with this template; null leaves it to the details drawer after Submit. */
  template?: SessionTemplate | null;
  /** "Record without a patient": ignore the sidebar selection too. */
  unassigned?: boolean;
  /** Properties for ``quick_start_started``, sent once the recording has really begun. */
  startedEvent?: Record<string, unknown>;
  /** Called when the start attempt is over, started or not. */
  onSettled?: () => void;
};
